import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { LayoutDashboard, Package, Users, Flag, SlidersHorizontal, FileText, ScrollText, ArrowLeft, MapPin, Sparkles, ImageIcon, BarChart3, Megaphone, Mail } from 'lucide-react';
import DashboardPanel from '@/components/admin/DashboardPanel';
import PostsPanel from '@/components/admin/PostsPanel';
import UsersPanel from '@/components/admin/UsersPanel';
import SettingsPanel from '@/components/admin/SettingsPanel';
import SiteContentPanel from '@/components/admin/SiteContentPanel';
import WelcomePanel from '@/components/admin/WelcomePanel';
import AuditPanel from '@/components/admin/AuditPanel';
import ModerationStatsPanel from '@/components/admin/ModerationStatsPanel';
import VisitsPanel from '@/components/admin/VisitsPanel';
import BroadcastPanel from '@/components/admin/BroadcastPanel';
import EmailSettingsPanel from '@/components/admin/EmailSettingsPanel';
import ModerationPage from '@/pages/ModerationPage';

const ROLE_LEVELS = { user: 0, moderator: 1, admin: 2, owner: 3 };
const NAV = [
  { key: 'dashboard', label: 'Dashboard', Icon: LayoutDashboard, min: 'moderator' },
  { key: 'posts', label: 'Posts', Icon: Package, min: 'moderator' },
  { key: 'reports', label: 'Reports', Icon: Flag, min: 'moderator' },
  { key: 'users', label: 'Users', Icon: Users, min: 'moderator' },
  { key: 'settings', label: 'Categories & Settings', Icon: SlidersHorizontal, min: 'admin' },
  { key: 'moderation', label: 'Image Moderation', Icon: ImageIcon, min: 'admin' },
  { key: 'visits', label: 'Site Visits', Icon: BarChart3, min: 'admin' },
  { key: 'content', label: 'Site Content', Icon: FileText, min: 'admin' },
  { key: 'welcome', label: 'Welcome Screen', Icon: Sparkles, min: 'admin' },
  { key: 'audit', label: 'Audit Log', Icon: ScrollText, min: 'admin' },
  { key: 'broadcast', label: 'Broadcast Push', Icon: Megaphone, min: 'owner' },
  { key: 'emails', label: 'Email Settings', Icon: Mail, min: 'owner' },
];

export default function AdminPage({ initialTab = 'dashboard' }) {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const level = ROLE_LEVELS[user?.role] ?? 0;
  const visibleNav = NAV.filter((n) => level >= ROLE_LEVELS[n.min]);
  const [tab, setTab] = useState(initialTab);

  useEffect(() => {
    if (!loading && (!user || level < 1)) navigate('/');
  }, [user, loading, level, navigate]);

  useEffect(() => {
    // If the requested tab isn't permitted for this role, fall back to the first allowed.
    if (visibleNav.length && !visibleNav.some((n) => n.key === tab)) setTab(visibleNav[0].key);
  }, [visibleNav, tab]);

  if (loading || !user || level < 1) {
    return <div className="h-screen flex items-center justify-center text-brand-ink">Loading…</div>;
  }

  return (
    <div className="min-h-screen bg-brand-paper" data-testid="admin-page">
      <div className="max-w-7xl mx-auto flex flex-col md:flex-row">
        {/* Sidebar */}
        <aside className="md:w-64 md:min-h-screen md:border-r border-black/5 p-4 sm:p-6 md:sticky md:top-0">
          <Link to="/" className="inline-flex items-center gap-2 text-brand-slate hover:text-brand-ink mb-6 text-sm" data-testid="back-to-map">
            <ArrowLeft className="w-4 h-4" /> Back to map
          </Link>
          <div className="flex items-center gap-2 mb-6">
            <MapPin className="w-5 h-5 text-brand" />
            <span className="font-display font-black text-xl text-brand-ink">Admin</span>
          </div>
          <nav className="flex md:flex-col gap-1 overflow-x-auto">
            {visibleNav.map(({ key, label, Icon }) => (
              <button
                key={key}
                data-testid={`admin-nav-${key}`}
                onClick={() => setTab(key)}
                className={`flex items-center gap-3 rounded-2xl px-4 py-2.5 text-sm font-medium whitespace-nowrap transition ${tab === key ? 'bg-brand text-white shadow' : 'text-brand-ink hover:bg-black/5'}`}
              >
                <Icon className="w-4 h-4" /> {label}
              </button>
            ))}
          </nav>
        </aside>

        {/* Content */}
        <main className="flex-1 p-5 sm:p-8 min-w-0">
          {tab === 'dashboard' && <DashboardPanel />}
          {tab === 'posts' && <PostsPanel />}
          {tab === 'users' && <UsersPanel />}
          {tab === 'reports' && <ModerationPage embedded />}
          {tab === 'settings' && <SettingsPanel />}
          {tab === 'visits' && <VisitsPanel />}
          {tab === 'moderation' && <ModerationStatsPanel />}
          {tab === 'content' && <SiteContentPanel />}
          {tab === 'welcome' && <WelcomePanel />}
          {tab === 'audit' && <AuditPanel />}
          {tab === 'broadcast' && <BroadcastPanel />}
          {tab === 'emails' && <EmailSettingsPanel />}
        </main>
      </div>
    </div>
  );
}
